import React, { useState, useEffect, useContext } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SellerHeader from "../../components/SellerHeader";
import { Link, useNavigate } from "react-router-dom";
import api from "../../utils/axiosInstance";
import { SellerContext } from "../../context/sellerContext";

const SellerProfile = () => {
  const { sellerId, ready } = useContext(SellerContext);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSellerProfile = async () => {
      setLoading(true);
      setError("");

      try {
        if (ready && !sellerId) {
          setError("No token found. Please login.");
          setTimeout(() => navigate("/seller"), 2000);
          return;
        }

        const { data } = await api.get(`/api/shop/seller-profile`, {
          withCredentials: true,
        });

        if (data.success) {
          setSeller(data.seller);
        } else {
          setError(data.message || "Failed to load seller profile");
          setTimeout(() => navigate("/seller"), 2000);
        }
      } catch (error) {
        console.error("Error fetching seller profile:", error);
        setError("Failed to load seller profile");
        setTimeout(() => navigate("/seller"), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerProfile();
  }, [navigate]);

  const handleToggleShopStatus = async () => {
    try {
      const updatedStatus = !seller?.shop?.isOpen;
      await api.put(
        `/api/shop/update-status`,
        { isOpen: updatedStatus },
        { withCredentials: true }
      );
      setSeller((prev) => ({
        ...prev,
        shop: { ...prev.shop, isOpen: updatedStatus },
      }));
      toast.success(`Shop is now ${updatedStatus ? "Open" : "Closed"}!`);
    } catch (error) {
      console.error("Error updating shop status:", error);
      toast.error("Failed to update shop status.");
    }
  };

  const handleGenerateQr = async () => {
    try {
      setQrLoading(true);
      const res = await api.post(
        `/api/shop/generate-qr`,
        {},
        { withCredentials: true }
      );

      if (res.data.success) {
        toast.success("QR Code generated successfully!");
        setSeller((prev) => ({
          ...prev,
          qrCode: res.data.qrCode,
        }));
      } else {
        toast.error(res.data.message || "Failed to generate QR Code.");
      }
    } catch (error) {
      console.error("QR generation error:", error);
      toast.error("Failed to generate QR Code.");
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQRCode = (qrCodeData, shopName) => {
    const link = document.createElement("a");
    link.href = qrCodeData;
    link.download = `${shopName || "shop"}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div className="text-center text-lg">Loading...</div>;

  if (error)
    return (
      <div className="text-center text-red-600 text-lg bg-red-100 p-4 rounded-lg">
        {error}
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <ToastContainer position="top-right" autoClose={3000} />
      <SellerHeader />

      <div className="container mx-auto py-8 px-4 space-y-8">
        <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-500 mb-4">
          Seller Dashboard
        </h1>

        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold mb-4">Seller Info</h2>
              <div className="space-y-2">
                <p>
                  <strong>Username:</strong> {seller?.username || "N/A"}
                </p>
                <p>
                  <strong>Email:</strong> {seller?.email || "N/A"}
                </p>
              </div>
              <Link
                to="/seller/edit-profile"
                className="mt-4 inline-block bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition"
              >
                Edit Profile
              </Link>
            </div>

            {/* Analytics */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <h2 className="text-2xl font-bold mb-4">Analytics</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-100 dark:bg-purple-700 p-4 rounded-lg">
                  <p className="text-sm">Monthly Sales</p>
                  <p className="text-xl font-bold">
                    ₹{seller.monthlySales || 0}
                  </p>
                </div>
                <div className="bg-green-100 dark:bg-green-700 p-4 rounded-lg">
                  <p className="text-sm">Active Orders</p>
                  <p className="text-xl font-bold">
                    {seller?.activeOrders || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-6">
            {seller?.shop ? (
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md space-y-4">
                <h2 className="text-2xl font-bold">Shop Info</h2>
                <p>
                  <strong>Name:</strong> {seller.shop.name || "N/A"}
                </p>
                <p>
                  <strong>Category:</strong> {seller.shop.category || "N/A"}
                </p>
                <p>
                  <strong>Products:</strong>{" "}
                  {seller.shop.productCategories?.join(", ") || "N/A"}
                </p>
                <p>
                  <strong>Address:</strong>{" "}
                  {seller.shop.address?.addressLine || "N/A"},{" "}
                  {seller.shop.address?.city || "N/A"}
                </p>

                <div className="flex items-center space-x-3">
                  <span
                    className={`px-3 py-1 rounded-full text-white ${
                      seller.shop.isOpen ? "bg-green-500" : "bg-red-500"
                    }`}
                  >
                    {seller.shop.isOpen ? "Open" : "Closed"}
                  </span>
                  {seller.shop.isManuallyClosed && (
                    <span className="px-2 py-1 bg-yellow-500 text-gray-900 rounded-full text-sm">
                      Manually Closed
                    </span>
                  )}
                </div>

                <button
                  onClick={handleToggleShopStatus}
                  className={`mt-4 w-full py-2 rounded-lg font-semibold text-white ${
                    seller.shop.isOpen
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-green-600 hover:bg-green-700"
                  } transition`}
                >
                  {seller.shop.isOpen ? "Close Shop" : "Open Shop"}
                </button>

                {/* ✅ QR Code Section */}
                <div className="mt-6 border-t border-gray-300 pt-4">
                  <h3 className="text-xl font-semibold mb-3">Shop QR Code</h3>
                  {seller.qrCode ? (
                    <div className="flex flex-col items-center space-y-3">
                      <img
                        src={seller.qrCode}
                        alt="Shop QR"
                        className="w-40 h-40 object-contain border rounded-lg p-2 shadow-md"
                      />
                      <button
                        onClick={() => downloadQRCode(seller.qrCode, seller.shopName)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        Download QR
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateQr}
                      disabled={qrLoading}
                      className="w-full bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {qrLoading ? "Generating..." : "Generate QR Code"}
                    </button>
                  )}
                </div>

                {/* Shop Images */}
                <div className="mt-6">
                  <h3 className="text-xl font-semibold mb-3">Shop Images</h3>
                  {seller.shop.images?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {seller.shop.images.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Shop ${index + 1}`}
                          className="w-full h-40 object-cover rounded-lg shadow-md hover:scale-105 transition-transform"
                          loading="lazy"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">
                      No Images Available
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-400 p-4 rounded-lg">
                No shop details found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerProfile;
